/**
 * This module defines the Upload action and its bindings to an `ItemsScope`.
 */

import * as React from 'react';
import { folderFacet } from '../../../facets/ItemFacets';
import { IUploaderProps, Uploader, IOnItemUploadedProps } from '../../uploader/Uploader';
import { withWizard, IDecorateWithWizardProps, IWithWizardProps } from '../../decorators/withWizard';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { uploadItemsActionControl } from '../../../controls/uploadItemsActionControl';
import { withOnlyCurrentItem, IDecorateWithOnlyCurrentItemProps } from '../../decorators/selectors';
import { IDecoratedProps } from '../../../utilities/components/Decorators';
import { IEngagementData } from '../../../facets/LoggerFacets';
import { graft } from '../../../utilities/Graft';
import { getItemActionEngagement } from '../../../models/telemetry/getItemEngagement';

export type IUploadWizardProps = IDecorateWithWizardProps<IUploaderProps>;

const UploadWizard: React.ComponentType<IUploadWizardProps> = withWizard(undefined, {
    isBackgroundTask: true,
    backgroundTaskScope: 'headless'
})(Uploader);

export type IItemUploadActionProps = IDecoratedProps<IUploadWizardProps,
    IWithWizardProps,
    {
        engagement?: Partial<IEngagementData> | undefined;
    }>;

export const ItemUploadAction: React.ComponentType<IItemUploadActionProps> = connectToItemCache((
    itemCache: IItemCache,
    props: {
        itemKey: string | undefined;
        engagement?: IEngagementData | undefined;
        onItemUploaded?: (props: IOnItemUploadedProps) => void;
    }
): IWithWizardProps & Pick<IUploadWizardProps, 'onItemUploaded'> => {
    const {
        itemKey,
        engagement = {},
        onItemUploaded: onItemUploadedCallback
    } = props;

    const controlHandler = itemCache.demandItemFacet(uploadItemsActionControl, itemKey);

    const {
        folder
    } = itemCache.demandItem({
        folder: folderFacet
    }, props.itemKey);

    const { dispatch } = itemCache;

    const defaultOnItemUploaded = onItemUploadedCallback ? (itemUploadedProps: IOnItemUploadedProps) => {
        return () => {
            onItemUploadedCallback(itemUploadedProps);
        };
    } : undefined;

    const {
        isAvailable,
        onItemUploaded
    } = uploadItemsActionControl.evaluate(controlHandler)(itemCache, {
        itemKey,
        isAvailable: !!folder,
        onItemUploaded: defaultOnItemUploaded
    });

    return {
        isAvailable,
        ...getItemActionEngagement(itemCache, {
            itemKey,
            engagement: graft({
                name: 'Upload',
                isIntentional: true
            }, engagement)
        }),
        onItemUploaded: onItemUploaded ? (itemUploadedProps: IOnItemUploadedProps) => {
            dispatch(onItemUploaded(itemUploadedProps));
        } : onItemUploadedCallback
    };
})(UploadWizard);

export type IUploadActionProps = IDecorateWithOnlyCurrentItemProps<IItemUploadActionProps>;

export const UploadAction: React.ComponentType<IUploadActionProps> = withOnlyCurrentItem()(ItemUploadAction);
